// src/features/captions/useCaptionMutations.ts

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { authKeys } from '@/features/auth/queryKeys';
import { CAPTIONS_QUERY_KEYS } from '@/lib/constants/captions';
import { CaptionsApi } from './CaptionsApi';
import type { CaptionGenerateReq } from './types';

/** Replace the ACCOUNT-scope term set. */
export function useSaveAccountCaptionTerms() {
  const { selectedCId } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (terms: string[]) => CaptionsApi.putAccountTermSet(selectedCId!, terms),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CAPTIONS_QUERY_KEYS.termSets(selectedCId!) });
    },
  });
}

/** Clear the ACCOUNT-scope term set. */
export function useDeleteAccountCaptionTerms() {
  const { selectedCId } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => CaptionsApi.deleteAccountTermSet(selectedCId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CAPTIONS_QUERY_KEYS.termSets(selectedCId!) });
    },
  });
}

/** Replace one media-stream-scope term set. */
export function useSaveStreamCaptionTerms() {
  const { selectedCId } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ mediaId, terms }: { mediaId: number; terms: string[] }) =>
      CaptionsApi.putStreamTermSet(selectedCId!, mediaId, terms),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CAPTIONS_QUERY_KEYS.termSets(selectedCId!) });
    },
  });
}

/** Remove one media-stream-scope term set. */
export function useDeleteStreamCaptionTerms() {
  const { selectedCId } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (mediaId: number) => CaptionsApi.deleteStreamTermSet(selectedCId!, mediaId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CAPTIONS_QUERY_KEYS.termSets(selectedCId!) });
    },
  });
}

/**
 * AI term research — synchronous server call running 30–150 s (no client
 * timeout; see CaptionsApi.generateTerms). Refreshes the generations history
 * whether it succeeds or fails: failed runs are recorded server-side too.
 */
export function useGenerateCaptionTerms() {
  const { selectedCId } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (req: CaptionGenerateReq) => CaptionsApi.generateTerms(selectedCId!, req),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: CAPTIONS_QUERY_KEYS.generations(selectedCId!) });
    },
  });
}

/**
 * Parent grant/revoke of captions for a sub-account — instant-save (never part
 * of the sub-account form's dirty state). Invalidates customerInfo, the source
 * of every subcustomer's quota features.
 */
export function useSetSubaccountCaptionGrant() {
  const { authData } = useAuth();
  const queryClient = useQueryClient();
  const userCId = authData?.cpuser.core.cId ?? null;

  return useMutation({
    mutationFn: ({ childCId, allow }: { childCId: number; allow: boolean }) =>
      CaptionsApi.setSubaccountCaptionGrant(childCId, allow),
    onSuccess: () => {
      if (userCId) {
        queryClient.invalidateQueries({ queryKey: authKeys.customerInfo(userCId) });
      }
    },
  });
}
